import React, { useEffect, useMemo, useState } from "react";
import TableContainer from "Common/TableContainer";
import { Button, Col, Form, Row, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment";
import {
  deleteProductById,
  getAllProducts,
  getTaxes,
  updateGoldPrice,
} from "api/apis";
import { Product, Tax } from "interface";

type Karat = "18K" | "22K" | "24K";

type GoldPrices = {
  [key in Karat]: string;
};

const ProductTable = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [allProducts, setAllProducts] = useState<Product[]>([]); // Store all products for search
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [goldPrices, setGoldPrices] = useState({
    "18K": "",
    "22K": "",
    "24K": "",
  });
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [taxLastUpdated, setTaxLastUpdated] = useState<number>(Date.now());
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const interval = setInterval(() => {
      setTaxLastUpdated(Date.now());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const taxesData = await getTaxes();
        setTaxes(taxesData);
      } catch (error) {
        console.error("Failed to fetch taxes:", error);
      }
    };
    fetchTaxes();
  }, [taxLastUpdated]);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    count: 0,
  });

  useEffect(() => {
    fetchProducts(pagination.page, pagination.limit);
  }, []);

  // Fetch all products when search term is entered
  useEffect(() => {
    if (searchTerm.trim()) {
      fetchAllProducts();
    } else {
      fetchProducts(pagination.page, pagination.limit);
    }
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
    fetchProducts(page, pagination.limit);
  };

  const handlePageSizeChange = (limit: number) => {
    const newPagination = {
      page: 1,
      limit,
      count: pagination.count,
      totalPages: Math.ceil(pagination.count / limit),
    };
    setPagination(newPagination);
    fetchProducts(1, limit);
  };

  const fetchProducts = async (page: number, limit: number) => {
    try {
      const { products, totalCount, currentPage, totalPages } =
        await getAllProducts(page, limit);

      setProducts(products);
      setPagination((prev) => ({
        ...prev,
        page: currentPage,
        limit,
        count: totalCount,
        totalPages: totalPages,
      }));
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchAllProducts = async () => {
    try {
      // Fetch all products by using a very large limit
      const { products, totalCount } = await getAllProducts(1, 10000);
      setAllProducts(products);
      setPagination((prev) => ({
        ...prev,
        count: totalCount,
      }));
    } catch (error) {
      console.error("Error fetching all products:", error);
    }
  };

  const handleInputChange = (karat: "18K" | "22K" | "24K", value: string) => {
    setGoldPrices((prev) => ({ ...prev, [karat]: value }));
  };

  const handleSaveGoldPrice = async () => {
    try {
      const filteredPrices = Object.entries(goldPrices)
        .filter(([, val]) => val !== "")
        .reduce((obj, [k, v]) => ({ ...obj, [k]: Number(v) }), {});

      if (Object.keys(filteredPrices).length === 0) {
        alert("Please enter at least one gold price.");
        return;
      }

      await updateGoldPrice({ prices: filteredPrices });
      alert("Gold price(s) updated successfully.");
      await fetchProducts(pagination.page, pagination.limit);
    } catch (error) {
      console.error("Error updating gold price:", error);
      alert("Failed to update gold price.");
    }
  };

  const handleValidDate = (date: any) =>
    moment(new Date(date)).format("DD MMM Y");

  const handleValidTime = (time: any) => {
    const time1 = new Date(time);
    const getHour = time1.getUTCHours();
    const getMin = time1.getUTCMinutes();
    const getTime = `${getHour}:${getMin}`;
    const meridiem = getHour >= 12 ? "PM" : "AM";
    return moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
  };

  const parentColumns = useMemo(
    () => [
      {
        Header: "#",
        Cell: (cell: any) => (
          <span className="text-muted">{cell.row.original.sku}</span>
        ),
      },
      {
        Header: "Product Info",
        Cell: (product: any) => (
          <div className="d-flex align-items-center">
            <div className="flex-shrink-0 me-2">
              <div className="avatar-sm bg-light rounded p-1">
                <img
                  src={product.row.original.images[0] || ""}
                  alt=""
                  className="img-fluid d-block avatar-xs"
                />
              </div>
            </div>
            <div className="flex-grow-1">
              <h5 className="fs-14 mb-1">
                <Link
                  to={`/product-create/${product.row.original._id}`}
                  className="d-block text-reset"
                >
                  {product.row.original.name}
                </Link>
              </h5>
            </div>
          </div>
        ),
      },
      {
        Header: "Variants",
        accessor: "variants",
        Cell: ({ value }: { value: any[] }) => (
          <span className="badge bg-primary">{value.length}</span>
        ),
      },
      {
        Header: "Actions",
        Cell: (cellProps: any) => (
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="ghost-primary"
              size="sm"
              className="btn-icon"
              onClick={() => {
                setSelectedProduct(cellProps.row.original);
                setShowVariantModal(true);
              }}
              title="View Variants"
            >
              <i className="ri-eye-fill"></i>
            </Button>
            <Button
              variant="ghost-info"
              size="sm"
              className="btn-icon"
              as={Link as any}
              to={`/product-create/${cellProps.row.original._id}`}
              title="Edit Product"
            >
              <i className="ri-pencil-fill"></i>
            </Button>
            <Button
              variant="ghost-danger"
              size="sm"
              className="btn-icon"
              onClick={async () => {
                const confirmDelete = window.confirm(
                  `Are you sure you want to delete this product and all its variants?`
                );
                if (confirmDelete) {
                  try {
                    await deleteProductById(cellProps.row.original._id);
                    alert("Product deleted successfully!");
                    fetchProducts(pagination.page, pagination.limit);
                  } catch (error) {
                    console.error("Error deleting product:", error);
                    alert("Failed to delete product");
                  }
                }
              }}
              title="Delete Product"
            >
              <i className="ri-delete-bin-fill"></i>
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const variantColumns = useMemo(
    () => [
      {
        Header: "Variant SKU",
        accessor: "sku",
      },
      {
        Header: "Price",
        accessor: "price",
        Cell: ({ value }: { value: number }) => `₹${value.toFixed(2)}`,
      },
      {
        Header: "Stock",
        accessor: "quantity",
        Cell: ({ value }: { value: number }) => (
          <span className={value < 5 ? "text-danger fw-bold" : ""}>
            {value}
          </span>
        ),
      },
      {
        Header: "Attributes",
        Cell: ({ row }: { row: any }) => {
          const attributes = row.original.attributes || [];
          return (
            <div>
              {attributes.map((attr: any) => (
                <div key={attr.label}>
                  {attr.label}: {attr.value}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => (
          <div className="d-flex justify-content-center gap-2">
            <Button
              variant="ghost-info"
              size="sm"
              className="btn-icon"
              as={Link as any}
              to={`/product-edit/${cellProps.row.original._id}/variant/${cellProps.row.original.variantId}`}
            >
              <i className="ri-pencil-fill"></i>
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) return products;

    // Search from all products when searching
    return allProducts.filter(
      (product) =>
        product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (typeof product.category === "string" &&
          product.category.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [searchTerm, products, allProducts]);

  return (
    <React.Fragment>
      <Row className="g-3 mb-4 align-items-center">
        <Col className="col-auto">
          <Link to="/product-create" className="btn btn-success">
            <i className="ri-add-line align-bottom me-1"></i> Add Product
          </Link>
        </Col>
        <Col className="col-sm">
          <div className="d-flex justify-content-sm-end">
            <div className="search-box ms-2">
              <Form.Control
                type="text"
                autoComplete="off"
                id="searchProductList"
                placeholder="Search Products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <i className="ri-search-line search-icon"></i>
            </div>
          </div>
        </Col>
      </Row>

      <TableContainer
        columns={parentColumns}
        data={filteredProducts}
        customPageSize={pagination.limit}
        tableClass="gridjs-table"
        theadClass="gridjs-th"
        isProductsFilter={true}
        SearchPlaceholder="Search Products..."
        manualPagination={!searchTerm.trim()} // Disable pagination when searching
        pageCount={pagination.totalPages}
        currentPage={pagination.page - 1}
        onPageChange={(page) => handlePageChange(page + 1)}
        onPageSizeChange={handlePageSizeChange}
      />

      {/* Variant Modal */}
      <Modal
        show={showVariantModal}
        onHide={() => setShowVariantModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Variants of {selectedProduct?.name}
            <span className="badge bg-primary ms-2">
              {selectedProduct?.variants?.length || 0}
            </span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-striped table-hover align-middle table-nowrap mb-0">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th style={{ paddingLeft: "12rem" }}>Attributes</th>
                </tr>
              </thead>
              <tbody>
                {selectedProduct?.variants?.map((variant) => (
                  <tr key={variant._id}>
                    <td>{variant.sku}</td>
                    <td>₹{variant.price?.toFixed(2)}</td>
                    <td
                      className={
                        variant.quantity < 5 ? "text-danger fw-bold" : ""
                      }
                    >
                      {variant.quantity}
                    </td>
                    <td
                      style={{
                        borderLeft: "2px solid rgba(255, 255, 255, 0.1)",
                        paddingLeft: "40px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                        }}
                      >
                        {variant.attributes?.map((attr, index) => (
                          <div
                            key={index}
                            style={{
                              width: "50%",
                              padding: "8px",
                              boxSizing: "border-box",
                              display: "flex",
                              gap: "6px",
                              border: "1px solid rgba(255, 255, 255, 0.1)",
                              borderRadius: "6px",
                              marginBottom: "8px",
                              backgroundColor: "#1e1e2d",
                            }}
                          >
                            <strong style={{ whiteSpace: "nowrap" }}>
                              {attr.label}:
                            </strong>
                            <span>{attr.value}</span>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowVariantModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default ProductTable;